/**
 * Hasło: zasady i generator (E1-S6).
 *
 * Zasady są **dokładnie te, które wymusi serwer** — Keycloak ma politykę
 * `length(12) and notUsername(undefined) and passwordHistory(3)`, a nasze API dokłada
 * górną granicę 128 znaków. Lista w przeglądarce ma uprzedzać odmowę, a nie obiecywać
 * czegoś, czego serwer potem nie przyjmie: hasło odrzucone po kliknięciu „Dalej” jest
 * gorsze niż brak podpowiedzi, bo człowiek nie wie, co poprawić.
 */

export type PasswordRule = 'length' | 'notEmail' | 'maxLength';

/** Które zasady hasło już spełnia. Kolejność jest stała, bo lista ma nie skakać przy pisaniu. */
export function checkPassword(password: string, email: string): Record<PasswordRule, boolean> {
  const address = email.trim().toLowerCase();
  return {
    length: password.length >= 12,
    // Keycloak porównuje hasło z nazwą użytkownika, którą u nas jest adres e-mail
    notEmail: address === '' || password.toLowerCase() !== address,
    maxLength: password.length <= 128,
  };
}

export function passwordAccepted(password: string, email: string): boolean {
  return Object.values(checkPassword(password, email)).every(Boolean);
}

/**
 * Znaki generowanego hasła.
 *
 * Bez `I`, `l`, `O` i `0` — hasło bywa przepisywane z ekranu na kartkę i z powrotem,
 * a te cztery znaki są wtedy nie do odróżnienia. Bez znaków przestankowych, bo nie są
 * wymagane, a psują przepisywanie i kopiowanie między systemami.
 */
const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';

/**
 * Hasło z generatora sprzętowego przeglądarki (`crypto.getRandomValues`), nigdy z `Math.random`.
 *
 * Odrzucamy losowania spoza pełnych wielokrotności długości alfabetu — bez tego reszta
 * z dzielenia faworyzowałaby pierwsze znaki alfabetu i hasło byłoby słabsze, niż wygląda.
 *
 * 20 znaków z 56-znakowego alfabetu to około 116 bitów. Z zapasem wystarczy.
 */
export function generatePassword(length = 20): string {
  const limit = Math.floor(256 / ALPHABET.length) * ALPHABET.length;
  const out: string[] = [];
  const buffer = new Uint8Array(length * 2);

  while (out.length < length) {
    crypto.getRandomValues(buffer);
    for (const byte of buffer) {
      if (byte < limit) {
        out.push(ALPHABET[byte % ALPHABET.length]);
        if (out.length === length) break;
      }
    }
  }
  return out.join('');
}
